import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import {
    ColumnMapping, IMPORT_FIELDS, ImportField, SheetRow, Spreadsheet, columnSamples,
} from '@functions/read-spreadsheet';

export const FIELD_LABEL: Record<ImportField, string> = {
    name: 'general.nome',
    phone: 'people.telefone',
    email: 'people.email',
    notes: 'people.observacoes',
};

/// Quantas linhas do topo podem ser escolhidas como cabeçalho.
const HEADER_OPTIONS = 20;

interface Props {
    allRows: SheetRow[];
    sheet: Spreadsheet;
    mapping: ColumnMapping;
    onHeaderLine: (line: number) => void;
    onMapping: (mapping: ColumnMapping) => void;
}

/// Só aparece se o palpite não achou nome e telefone, ou se pedirem ajuste.
export const ImportColumnsStep = ({ allRows, sheet, mapping, onHeaderLine, onMapping }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const fieldOf = (column: number) =>
        IMPORT_FIELDS.find((field) => mapping[field] === column) ?? null;

    /// Um campo vem de uma coluna só: escolher "Telefone" aqui tira o telefone de onde estava.
    const assign = (column: number, field: ImportField | null) => {
        const next = { ...mapping };

        IMPORT_FIELDS.forEach((item) => {
            if (next[item] === column) next[item] = null;
        });

        if (field) next[field] = column;

        onMapping(next);
    };

    const fieldOptions = IMPORT_FIELDS.map((field) => ({
        value: field,
        label: t(FIELD_LABEL[field]) + (field === 'name' || field === 'phone' ? ' *' : ''),
    }));

    const headerOptions = allRows.slice(0, HEADER_OPTIONS).map((row) => ({
        value: row.line,
        label: t('people.importar.linhaComConteudo', {
            linha: row.line,
            conteudo: row.cells.filter((cell) => cell !== '').slice(0, 4).join(', '),
        }),
    }));

    const cell = { px: 2, py: 1.5, borderTop: `1px solid ${cc.divider}` };

    return (
        <Box>
            <Box sx={{ maxWidth: 520, mb: 3 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: cc.textPrimary, mb: 0.5 }}>
                    {t('people.importar.cabecalho')}
                </Typography>
                <Typography sx={{ fontSize: 13, color: cc.textSecondary, mb: 1 }}>
                    {t('people.importar.cabecalhoAjuda')}
                </Typography>
                <StandaloneAutoCompleteSelect
                    options={headerOptions}
                    value={sheet.headerLine}
                    onChange={(value) => value && onHeaderLine(Number(value))}
                />
            </Box>

            <Typography sx={{ fontSize: 14, fontWeight: 600, color: cc.textPrimary, mb: 1 }}>
                {t('people.importar.colunas')}
            </Typography>

            <Box sx={{ border: `1px solid ${cc.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                <Box
                    sx={{
                        display: { xs: 'none', md: 'grid' },
                        gridTemplateColumns: '1fr 1.6fr 1fr',
                        background: cc.surfaceBg,
                    }}
                >
                    {['colunaDaPlanilha', 'exemplos', 'campoNoCrm'].map((key) => (
                        <Typography
                            key={key}
                            sx={{ px: 2, py: 1.25, fontSize: 12, fontWeight: 700, color: cc.textMuted, textTransform: 'uppercase' }}
                        >
                            {t(`people.importar.${key}`)}
                        </Typography>
                    ))}
                </Box>

                {sheet.headers.map((header, column) => {
                    const samples = columnSamples(sheet, column);
                    const field = fieldOf(column);

                    return (
                        <Box
                            key={column}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1.6fr 1fr' },
                                alignItems: 'center',
                                ...cell,
                            }}
                        >
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: header ? cc.textPrimary : cc.textMuted }}>
                                {header || t('people.importar.colunaSemNome', { numero: column + 1 })}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: cc.textSecondary,
                                    pr: 2,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {samples.length > 0 ? samples.join(' · ') : t('people.importar.colunaVazia')}
                            </Typography>

                            <Box sx={{ mt: { xs: 1, md: 0 } }}>
                                <StandaloneAutoCompleteSelect
                                    isClearable
                                    options={fieldOptions}
                                    value={field}
                                    placeholder={t('people.importar.naoImportar')}
                                    onChange={(value) => assign(column, (value as ImportField) ?? null)}
                                />
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};
